import React, { useLayoutEffect, useRef, useState } from 'react';
import { formatMoney, getNumberSeparators, groupIntDigits } from '../../utils/numberFormat';

// Region-formatted money entry. The user types and reads amounts in their
// region's separators (TR → 1.250.000,50, US → 1,250,000.50): thousands
// grouping is applied live while typing, and the decimal part is padded to
// two digits on blur. What leaves through onChange is always the canonical
// machine string with a dot decimal ("1250000.5") — payloads and stored
// values never carry regional formatting.
//
// `value` accepts the canonical string or a number; empty string/null = blank.

// canonical "1250000.5" → display "1.250.000,5" (while editing; blur pads it)
function toEditText(canonical) {
  if (canonical === null || canonical === undefined || canonical === '') return '';
  const s = String(canonical);
  const [intPart, decPart] = s.split('.');
  const { decimal } = getNumberSeparators();
  const grouped = groupIntDigits(intPart.replace(/\D/g, '') || '0');
  return decPart !== undefined ? `${grouped}${decimal}${decPart}` : grouped;
}

function toDisplayText(canonical) {
  if (canonical === null || canonical === undefined || canonical === '') return '';
  return formatMoney(canonical, '');
}

function MoneyInput({
  value, onChange, placeholder, required, disabled, id, name, className,
}) {
  const inputRef = useRef(null);
  const caretRef = useRef(null); // significant chars (digits + decimal sep) left of caret
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => toDisplayText(value));

  // While not editing, the field mirrors the outside value in full money
  // format (grouped + two decimals). While focused the user's typing owns it.
  const outside = focused ? null : toDisplayText(value);
  const shown = focused ? text : outside;

  useLayoutEffect(() => {
    if (caretRef.current === null || !inputRef.current) return;
    const target = caretRef.current;
    caretRef.current = null;
    const { decimal } = getNumberSeparators();
    let count = 0;
    let pos = 0;
    while (pos < text.length && count < target) {
      const ch = text[pos];
      if (/\d/.test(ch) || ch === decimal) count += 1;
      pos += 1;
    }
    inputRef.current.setSelectionRange(pos, pos);
  }, [text]);

  const handleChange = (e) => {
    const { decimal } = getNumberSeparators();
    const el = e.target;
    const raw = el.value;

    // Caret position measured in significant chars, so it survives regrouping
    const before = raw.slice(0, el.selectionStart);
    caretRef.current = (before.match(/\d/g) || []).length
      + (before.includes(decimal) ? 1 : 0);

    // Split on the FIRST decimal separator; everything else keeps digits only
    const sepIdx = raw.indexOf(decimal);
    const hasDec = sepIdx !== -1;
    const intDigits = (hasDec ? raw.slice(0, sepIdx) : raw).replace(/\D/g, '');
    const decDigits = hasDec ? raw.slice(sepIdx + 1).replace(/\D/g, '').slice(0, 2) : '';

    if (!intDigits && !hasDec) {
      setText('');
      onChange?.('');
      return;
    }

    const grouped = groupIntDigits(intDigits || '0');
    setText(hasDec ? `${grouped}${decimal}${decDigits}` : grouped);
    onChange?.(hasDec && decDigits ? `${intDigits || '0'}.${decDigits}` : (intDigits || '0'));
  };

  const handleFocus = () => {
    setFocused(true);
    setText(toEditText(value));
  };

  const handleBlur = () => {
    setFocused(false);
    caretRef.current = null;
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      id={id}
      name={name}
      className={className}
      value={shown ?? ''}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder ?? formatMoney(0, '')}
      required={required}
      disabled={disabled}
    />
  );
}

export default MoneyInput;
